import { useState, useEffect } from "react";
import { useBranding, useUserData } from "@/hooks/queries";
import { useThemeMode } from "@/hooks/useThemeMode";
import { useIsMobile } from "@/hooks/useResponsive";
import { UserMenu } from "@components/UserMenu";
import { ROUTER_PATHS } from "@constants/index";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { IconButton, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import fallbackLogoSvg from "@assets/Logo_CL.svg?raw";
import styles from "./styles.module.css";

export const Header = () => {
  const { data: userData } = useUserData();
  const { data: branding, isSuccess: brandingLoaded } = useBranding();
  const { mode, toggleMode } = useThemeMode();
  const isMobile = useIsMobile();
  const isUserData = !!userData;
  const name = userData?.full_name ?? "";
  const isAdmin = userData?.isAdmin;

  const [svgContent, setSvgContent] = useState<string | null>(null);

  useEffect(() => {
    if (branding?.logo_is_svg && branding.logo_url) {
      fetch(branding.logo_url)
        .then((r) => r.text())
        .then(setSvgContent)
        .catch(() => setSvgContent(null));
    } else {
      setSvgContent(null);
    }
  }, [branding?.logo_is_svg, branding?.logo_url]);

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>("link[rel='shortcut icon']");
    if (!link) return;
    const faviconUrl = branding?.logo_url;
    link.href = faviconUrl ?? "/favicon.ico";
  }, [branding?.logo_url]);

  const renderLogo = () => {
    const logoClass = `${styles.logo} ${isMobile ? styles.logoCompact : ""}`;

    if (branding?.logo_is_svg && svgContent) {
      return (
        <span
          className={logoClass}
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      );
    }

    if (branding?.logo_url && !branding.logo_is_svg) {
      const imgSrc = mode === "dark" && branding.logo_dark_url
        ? branding.logo_dark_url
        : branding.logo_url;
      return (
        <img
          src={imgSrc}
          alt="Logo"
          className={logoClass}
          style={{ maxHeight: 40, objectFit: "contain" }}
        />
      );
    }

    if (!brandingLoaded) return <span className={logoClass} />;

    return (
      <span
        className={logoClass}
        dangerouslySetInnerHTML={{ __html: fallbackLogoSvg }}
      />
    );
  };

  return (
    <header className={styles.header}>
      <Link
        to={ROUTER_PATHS.main}
        aria-label="Go home"
        className={name ? "" : styles.pointerNone}
      >
        {renderLogo()}
      </Link>
      <div className={styles.actions}>
        <Tooltip title={mode === "light" ? "Switch to dark mode" : "Switch to light mode"}>
          <IconButton onClick={toggleMode} color="inherit" aria-label="Toggle theme">
            {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>
        {isUserData && <UserMenu name={name} isAdmin={!!isAdmin} />}
      </div>
    </header>
  );
};
